import { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useChannels } from '@/hooks/useChannels';
import { cn } from '@/utils/cn';
import {
  Channel,
  PlatformType,
} from '@/services/channels.service';
import { checkInstagramAspect, type InstagramContentType } from '@/utils/igAspectValidator';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { formatApiError } from '@/services/api';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faGoogleDrive,
  faBluesky,
  faWordpress,
} from '@fortawesome/free-brands-svg-icons';
import { faCheck, faGlobe } from '@fortawesome/free-solid-svg-icons';
import {
  checkPlatformMediaCompatibility,
  checkMixedMediaCompatibility,
  getGifAutoConvertNotice,
  getGmbVideoThumbnailNotice,
  MediaType,
} from '@/constants/platformMediaRequirements';
import { isSocialPublishTarget } from '@/constants/platforms';
import { countCharactersForPlatform } from '@/utils/characterCount';
import { getPlatformLimit } from '@/constants/platformLimits';
import { getChannelAvatar } from '@/utils/channel-avatar';
import type { PlatformOptions } from '@/types/scheduled-instances';
import type { MediaItem } from '@/types/posts';
import { PlatformOptionsSection } from '@/components/PostComposer/sections/PlatformOptionsSection';
import { PlatformPreviewRouter } from '@/components/PostComposer/sections/preview';

/**
 * ChannelSelector Component
 *
 * Modern compact horizontal channel selector
 * - Flat horizontal layout for space efficiency
 * - 40px circular avatars with selection states
 * - Clean selection state with checkmark overlay
 */

interface ChannelSelectorProps {
  selectedChannels: number[];
  onSelectionChange: (channelIds: number[]) => void;
  disabled?: boolean;
  className?: string;
  /** Current media types in the post - used to filter compatible channels */
  mediaTypes?: Array<'IMAGE' | 'VIDEO' | 'PDF'>;
  /** Channel platform options for inline display under clusters */
  channelOptions?: Record<number, PlatformOptions>;
  onOptionsChange?: (channelId: number, options: PlatformOptions) => void;
  hasVideo?: boolean;
  hasMedia?: boolean;
  /** Caption for inline platform preview */
  caption?: string;
  /** Media for inline platform preview */
  media?: MediaItem[];
  isCarouselMode?: boolean;
  channels?: Array<{
    platform: string;
    displayName: string;
    handle: string;
    avatarUrl?: string;
    channelId: number;
    options?: PlatformOptions;
  }>;
}

/**
 * Platform configuration with colors and Font Awesome icons
 */
interface PlatformInfo {
  color: string;
  icon: IconDefinition;
  name: string;
}

const PLATFORM_INFO: Record<PlatformType, PlatformInfo> = {
  LINKEDIN: { color: '#0A66C2', icon: faLinkedin, name: 'LinkedIn' },
  FACEBOOK: { color: '#1877F2', icon: faFacebook, name: 'Facebook' },
  INSTAGRAM: { color: '#E4405F', icon: faInstagram, name: 'Instagram' },
  TWITTER: { color: '#000000', icon: faXTwitter, name: 'X (Twitter)' },
  YOUTUBE: { color: '#FF0000', icon: faYoutube, name: 'YouTube' },
  TIKTOK: { color: '#000000', icon: faTiktok, name: 'TikTok' },
  PINTEREST: { color: '#E60023', icon: faPinterest, name: 'Pinterest' },
  THREADS: { color: '#000000', icon: faThreads, name: 'Threads' },
  GOOGLE_MY_BUSINESS: { color: '#4285F4', icon: faGoogle, name: 'Google Business' },
  BLUESKY: { color: '#0085FF', icon: faBluesky, name: 'Bluesky' },
  // CANVA, GDRIVE, and WORDPRESS are non-social asset/blog channels — see
  // `NON_SOCIAL_CHANNEL_PLATFORMS` in '@/constants/platforms'. They're
  // filtered out of `publishableChannels` below via `isSocialPublishTarget`,
  // but these entries stay as a defensive fallback so the UI doesn't crash
  // if one of these channels ever appears in an unfiltered channel list.
  CANVA: { color: '#00C4CC', icon: faGlobe, name: 'Canva' },
  GDRIVE: { color: '#4285F4', icon: faGoogleDrive, name: 'Google Drive' },
  WORDPRESS: { color: '#21759B', icon: faWordpress, name: 'WordPress' },
};

/**
 * Get platform display info with fallback
 */
const getPlatformInfo = (platform: PlatformType): PlatformInfo => {
  return PLATFORM_INFO[platform] || { color: '#6B7280', icon: faGlobe, name: platform };
};

/**
 * ChannelAvatar Component - Compact avatar with selection state
 */
interface ChannelAvatarProps {
  channel: Channel;
  isSelected: boolean;
  /** Must be referentially stable (receives the channel id) so memo holds. */
  onToggle: (channelId: number) => void;
  disabled?: boolean;
  platformColor: string;
  platform: PlatformType;
  /** If set, channel is incompatible and shows reason in tooltip */
  incompatibilityReason?: string;
  /**
   * Non-blocking informational notice (e.g. GIF auto-conversion).
   * Channel stays fully selectable — only a subtle info dot is shown.
   */
  infoNotice?: string;
}

const ChannelAvatarBase = ({
  channel,
  isSelected,
  onToggle,
  disabled = false,
  platformColor,
  platform,
  incompatibilityReason,
  infoNotice,
}: ChannelAvatarProps) => {
  const avatarUrl = getChannelAvatar(channel.metadata as Record<string, unknown> | null | undefined) ?? undefined;
  const hasAvatar = !!avatarUrl;

  // Build tooltip: incompatibility reason takes priority, then info notice, then plain label
  const tooltipContent = incompatibilityReason
    ? `${getPlatformInfo(platform).name}: ${channel.platformUsername} - ${incompatibilityReason}`
    : infoNotice
      ? `${getPlatformInfo(platform).name}: ${channel.platformUsername} - ${infoNotice}`
      : `${getPlatformInfo(platform).name}: ${channel.platformUsername}`;

  return (
    <Tooltip content={tooltipContent}>
      <button
        type="button"
        onClick={incompatibilityReason && !isSelected ? undefined : () => onToggle(channel.id)}
        disabled={disabled}
        className={cn(
          'relative group',
          'w-10 h-10', // 40px - compact
          'rounded-full',
          'transition-all duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          isSelected && !incompatibilityReason && 'ring-2 ring-primary-500 ring-offset-2 scale-105 transform',
          isSelected && incompatibilityReason && 'ring-2 ring-red-500 ring-offset-2 scale-105 transform',
          !isSelected && !incompatibilityReason && 'opacity-70 hover:opacity-100 hover:scale-105',
          incompatibilityReason && !isSelected && 'opacity-40 cursor-not-allowed grayscale',
          incompatibilityReason && isSelected && 'opacity-90',
          disabled && !incompatibilityReason && 'cursor-not-allowed opacity-30'
        )}
        aria-label={`${isSelected ? 'Deselect' : 'Select'} ${channel.platformUsername}`}
        aria-pressed={isSelected}
      >
        {/* Avatar */}
        {hasAvatar && avatarUrl ? (
          <img
            src={avatarUrl}
            alt={channel.platformUsername}
            className="w-full h-full rounded-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        {/* Fallback - initials */}
        <div
          className={cn(
            'w-full h-full rounded-full flex items-center justify-center text-white text-xs font-semibold',
            hasAvatar ? 'hidden' : ''
          )}
          style={{ backgroundColor: platformColor }}
        >
          {channel.platformUsername?.[0]?.toUpperCase() || '?'}
        </div>

        {/* Incompatibility warning badge */}
        {incompatibilityReason && (
          <span className="absolute -top-0.5 -left-0.5 w-3.5 h-3.5 bg-amber-500 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-white text-[8px] font-bold">!</span>
          </span>
        )}

        {/* Non-blocking info badge (e.g. GIF auto-conversion notice) — no greyscale, no disabled */}
        {!incompatibilityReason && infoNotice && (
          <span className="absolute -top-0.5 -left-0.5 w-3.5 h-3.5 bg-secondary-500 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-secondary-500-contrast text-[8px] font-bold leading-none">i</span>
          </span>
        )}

        {/* Selection checkmark */}
        {isSelected && !incompatibilityReason && (
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-primary-500 rounded-full shadow-sm">
            <FontAwesomeIcon icon={faCheck} className="text-white text-[7px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </span>
        )}

        {/* Platform badge - bottom right */}
        <span
          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full shadow-sm border-2 border-white"
          style={{ backgroundColor: platformColor }}
        >
          <FontAwesomeIcon
            icon={getPlatformInfo(platform).icon}
            className="text-white text-[9px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </span>
      </button>
    </Tooltip>
  );
};

/**
 * Memoized: an avatar only depends on its own channel + selection/compat state,
 * not on caption/media. `channel` is re-spread on every selector render, so
 * compare by the fields actually rendered rather than by reference.
 */
const ChannelAvatar = memo(ChannelAvatarBase, (prev, next) =>
  prev.channel.id === next.channel.id &&
  prev.channel.platformUsername === next.channel.platformUsername &&
  prev.channel.metadata === next.channel.metadata &&
  prev.isSelected === next.isSelected &&
  prev.disabled === next.disabled &&
  prev.platformColor === next.platformColor &&
  prev.platform === next.platform &&
  prev.incompatibilityReason === next.incompatibilityReason &&
  prev.infoNotice === next.infoNotice &&
  prev.onToggle === next.onToggle
);

export const ChannelSelector = memo(function ChannelSelector({
  selectedChannels,
  onSelectionChange,
  disabled = false,
  className = '',
  mediaTypes,
  channelOptions,
  onOptionsChange,
  hasVideo,
  hasMedia,
  caption,
  media,
  isCarouselMode,
  channels: channelPreviewData,
}: ChannelSelectorProps) {
  const { t } = useTranslation('common');
  const { data: channels, isLoading, error } = useChannels();
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<PlatformType>>(new Set());
  const [previewPlatforms, setPreviewPlatforms] = useState<Set<PlatformType>>(new Set());

  const togglePreview = (platform: PlatformType) => {
    setPreviewPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) {
        next.delete(platform);
      } else {
        next.add(platform);
      }
      return next;
    });
  };

  const togglePlatformExpanded = (platform: PlatformType) => {
    setExpandedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) {
        next.delete(platform);
      } else {
        next.add(platform);
      }
      return next;
    });
  };

  // Stable toggle handler (reads latest props via ref) so memoized avatars
  // don't re-render just because selection of a sibling changed the closure.
  const toggleStateRef = useRef({ disabled, selectedChannels, onSelectionChange });
  toggleStateRef.current = { disabled, selectedChannels, onSelectionChange };
  const handleToggle = useCallback((channelId: number) => {
    const { disabled: isDisabled, selectedChannels: current, onSelectionChange: onChange } = toggleStateRef.current;
    if (isDisabled) return;
    if (current.includes(channelId)) {
      onChange(current.filter((id) => id !== channelId));
    } else {
      onChange([...current, channelId]);
    }
  }, []);

  const handleSelectAll = () => {
    // Only select compatible active channels
    const compatibleChannels = activeChannels.filter((c) => c.isCompatible);
    onSelectionChange(compatibleChannels.map((c) => c.id));
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  // Loading state - skeleton avatars
  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="flex justify-between items-center">
          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
          <div className="h-3 bg-gray-200 rounded w-32 animate-pulse" />
        </div>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="w-12 h-12 rounded-full bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={cn('text-center py-6', className)}>
        <Icon
          name={ICONS.CAUTION}
          size="lg"
          color="text-red-500"
          className="mx-auto mb-2"
        />
        <p className="text-red-600 text-sm font-medium">
          {t('composer_failed_load_channels')}
        </p>
        <p className="text-gray-500 text-xs mt-1">
          {formatApiError(error).message}
        </p>
      </div>
    );
  }

  // Empty state
  if (!channels || channels.length === 0) {
    return (
      <div className={cn('text-center py-6 bg-gray-50 rounded-lg', className)}>
        <Icon
          name={ICONS.CAUTION}
          size="lg"
          color="text-gray-400"
          className="mx-auto mb-2"
        />
        <p className="text-gray-600 text-sm font-medium mb-1">
          {t('composer_no_channels_connected')}
        </p>
        <p className="text-gray-500 text-xs mb-3">
          {t('composer_connect_channels_to_post')}
        </p>
        <Link
          to="/channels"
          className="text-primary-500 hover:text-primary-600 text-sm font-medium hover:underline"
        >
          {t('composer_connect_channel')}
        </Link>
      </div>
    );
  }

  // Filter out non-social asset/blog channels (CANVA, GDRIVE, WORDPRESS) and
  // check media compatibility. See NON_SOCIAL_CHANNEL_PLATFORMS for the
  // single source of truth on which platforms aren't social-post targets.
  const mediaTypesArray = mediaTypes || [];
  const mediaArray = media || [];
  const publishableChannels = channels.filter((c) => isSocialPublishTarget(c.platform));

  // Check compatibility for each channel
  const channelsWithCompatibility = publishableChannels.map((channel) => {
    // GIF auto-convert notice — informational only, does NOT affect compatibility.
    // Backend converts GIFs to MP4 (Instagram) or JPG (Google Business) at publish time.
    const gifNotice = getGifAutoConvertNotice(channel.platform, mediaArray);

    // GMB video→thumbnail notice — informational only, does NOT affect compatibility.
    // Backend posts the video's thumbnail image + caption on Google Business
    // Profile since GBP posts don't support actual video.
    const gmbVideoNotice = getGmbVideoThumbnailNotice(channel.platform, mediaArray);

    // Check media type compatibility (PDF, video-only platforms, etc.)
    const { isCompatible: mediaCompatible, reason: mediaReason } = checkPlatformMediaCompatibility(
      channel.platform,
      mediaTypesArray as MediaType[]
    );

    // Check mixed image+video compatibility using the full effective media array
    // (mediaArray resolves inherited variation media; mediaTypesArray is empty in inherit mode)
    const { isCompatible: mixedCompatible, reason: mixedReason } = checkMixedMediaCompatibility(
      channel.platform,
      mediaArray
    );

    // Check caption length against platform limit
    let captionReason: string | undefined;
    if (caption && caption.length > 0) {
      const platformKey = channel.platform.toLowerCase();
      const charLimit = getPlatformLimit(platformKey, 'characters');
      const charCount = countCharactersForPlatform(caption, platformKey);
      if (charCount > charLimit) {
        captionReason = t('caption_exceeds_limit', {
          count: charCount,
          limit: charLimit,
          defaultValue: `Caption exceeds limit (${charCount}/${charLimit} chars)`,
        });
      }
    }

    // Incompatibility priority: mixed-media check takes first position (clearest blocker),
    // then media-type mismatch, then caption overflow. GIF is non-blocking (infoNotice).
    const incompatibilityReason = mixedReason || mediaReason || captionReason;
    const isCompatible = mixedCompatible && mediaCompatible && !captionReason;

    return {
      ...channel,
      isCompatible,
      incompatibilityReason,
      // Non-blocking informational notice; undefined when not applicable.
      // GIF and GMB-video notices are mutually exclusive in practice (GIFs are
      // MediaItem type 'image'), so a simple fallback is sufficient.
      infoNotice: gifNotice ?? gmbVideoNotice,
    };
  });

  const activeChannels = channelsWithCompatibility.filter((c) => c.isActive);
  const inactiveChannels = channelsWithCompatibility.filter((c) => !c.isActive);

  return (
    <div className={className}>
      {/* Header with Select All / Deselect All */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-gray-500">
          {selectedChannels.length > 0 ? (
            <span className="text-primary-600 font-medium">
              {t('composer_channels_selected', { count: selectedChannels.length })}
            </span>
          ) : (
            t('composer_select_channels')
          )}
        </span>
        <div className="flex gap-1.5 text-[10px]">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled || activeChannels.length === 0}
            className="text-primary-500 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed max-sm:py-[17px] max-sm:-my-[17px] max-sm:px-2 max-sm:-mx-2"
          >
            {t('composer_select_all')}
          </button>
          <span className="text-gray-300">•</span>
          <button
            type="button"
            onClick={handleDeselectAll}
            disabled={disabled || selectedChannels.length === 0}
            className="text-gray-500 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed max-sm:py-[17px] max-sm:-my-[17px] max-sm:px-2 max-sm:-mx-2"
          >
            {t('composer_deselect_all')}
          </button>
        </div>
      </div>

      {/* Channels grouped by platform — each platform on its own row */}
      <div className="space-y-3">
        {(() => {
          const platformOrder: PlatformType[] = [];
          const grouped = new Map<PlatformType, typeof activeChannels>();
          for (const channel of activeChannels) {
            if (!grouped.has(channel.platform)) {
              platformOrder.push(channel.platform);
              grouped.set(channel.platform, []);
            }
            grouped.get(channel.platform)!.push(channel);
          }
          platformOrder.sort((a, b) => {
            const diff = (grouped.get(b)?.length ?? 0) - (grouped.get(a)?.length ?? 0);
            if (diff !== 0) return diff;
            return getPlatformInfo(a).name.localeCompare(getPlatformInfo(b).name);
          });

          return platformOrder.map((platform) => {
            const platformChannels = grouped.get(platform) ?? [];
            const { color, icon, name } = getPlatformInfo(platform);
            const isExpanded = expandedPlatforms.has(platform);
            const selectedCount = platformChannels.filter((c) => selectedChannels.includes(c.id)).length;
            const totalCount = platformChannels.length;

            const selectedPlatformChannels = platformChannels.filter((c) =>
                selectedChannels.includes(c.id)
              );
              const showPreviewToggle = selectedPlatformChannels.length > 0 && caption !== undefined && media !== undefined;
              const isPreviewOpen = previewPlatforms.has(platform);

              return (
              <div key={platform}>
                {/* Collapsible header row */}
                <div className="flex items-center gap-2.5 w-full">
                  {/* div[role=button] (not <button>) because it contains the preview-toggle <button>; nested buttons are invalid HTML */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onClick={() => togglePlatformExpanded(platform)}
                    onKeyDown={(e) => {
                      // Ignore keys bubbling up from the inner preview button
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        togglePlatformExpanded(platform);
                      }
                    }}
                    className="flex items-center gap-2.5 flex-1 min-w-0 group cursor-pointer"
                  >
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: color }}
                    >
                      <FontAwesomeIcon
                        icon={icon}
                        className="text-white"
                        style={{ fontSize: '12px' }}
                      />
                    </span>
                    <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900 transition-colors truncate">
                      {name}
                    </span>
                    {selectedCount > 0 && (
                      <span className="flex-shrink-0 text-[10px] font-medium text-primary-600 bg-primary-50 rounded-full px-1.5 py-0.5 leading-none">
                        {selectedCount}/{totalCount}
                      </span>
                    )}
                    {/* Eye icon for inline preview toggle — next to count */}
                    {showPreviewToggle && (
                      <Tooltip content={isPreviewOpen ? t('preview_channel_hide', { name }) : t('preview_channel_show', { name })}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); togglePreview(platform); }}
                          className={cn(
                            'flex-shrink-0 rounded transition-colors leading-none',
                            isPreviewOpen ? 'text-primary-500' : 'text-black/40 hover:text-black'
                          )}
                          aria-label={isPreviewOpen ? t('preview_channel_hide', { name }) : t('preview_channel_show', { name })}
                          aria-pressed={isPreviewOpen}
                        >
                          <span className="material-symbols-outlined leading-none align-middle" style={{ fontSize: 'clamp(12px, 1.6vh, 14px)' }}>visibility</span>
                        </button>
                      </Tooltip>
                    )}
                    <span className="flex-shrink-0 material-symbols-outlined text-gray-400 text-[14px] ml-auto transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      expand_more
                    </span>
                  </div>
                </div>
                {/* Expanded channel avatars */}
                {isExpanded && (
                  <div className="flex flex-wrap gap-2 mt-2 pl-[38px]">
                    {platformChannels.map((channel) => (
                      <ChannelAvatar
                        key={channel.id}
                        channel={channel}
                        isSelected={selectedChannels.includes(channel.id)}
                        onToggle={handleToggle}
                        disabled={disabled || !channel.isCompatible}
                        platformColor={color}
                        platform={channel.platform}
                        incompatibilityReason={channel.incompatibilityReason}
                        infoNotice={channel.infoNotice}
                      />
                    ))}
                  </div>
                )}
                {/* Inline Platform Options — shown when expanded and channels selected */}
                {isExpanded && selectedPlatformChannels.length > 0 && channelOptions !== undefined && onOptionsChange !== undefined && (
                  <div className="mt-2 pl-[38px]">
                    <PlatformOptionsSection
                      selectedChannels={selectedPlatformChannels}
                      channelOptions={channelOptions}
                      onOptionsChange={onOptionsChange}
                      disabled={disabled}
                      hasVideo={hasVideo}
                      hasMedia={hasMedia}
                    />
                  </div>
                )}
                {/* IG aspect-ratio warning — surfaces composer-time the same
                    constraint the publish worker enforces, so users don't
                    discover at publish time that their tall portrait can't go
                    to FEED. */}
                {platform === 'INSTAGRAM' && selectedPlatformChannels.length > 0 && media !== undefined && channelOptions !== undefined && onOptionsChange !== undefined && (
                  <IgAspectWarning
                    selectedChannels={selectedPlatformChannels}
                    channelOptions={channelOptions}
                    media={media}
                    onOptionsChange={onOptionsChange}
                    onWarningChange={(hasWarning) => {
                      // Auto-expand the IG section while a warning is live so
                      // users see channel avatars + content-type toggle next
                      // to the alert. (Manual collapse stays sticky once
                      // warnings clear.)
                      if (hasWarning && !expandedPlatforms.has('INSTAGRAM')) {
                        setExpandedPlatforms((prev) => new Set(prev).add('INSTAGRAM'));
                      }
                    }}
                    disabled={disabled}
                  />
                )}
                {/* Inline Platform Preview — shown when eye icon toggled */}
                {isPreviewOpen && caption !== undefined && media !== undefined && (
                  <div className="mt-2 pl-[38px]">
                    <PlatformPreviewRouter
                      platform={platform.toLowerCase()}
                      caption={caption}
                      media={media}
                      isCarouselMode={isCarouselMode ?? false}
                      channels={channelPreviewData}
                    />
                  </div>
                )}
              </div>
            );
          });
        })()}
      </div>

      {/* Inactive Channels Warning */}
      {inactiveChannels.length > 0 && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400">
              {t('composer_channels_disconnected', { count: inactiveChannels.length })}
            </span>
            {/* Inactive channel avatars - tiny and faded */}
            <div className="flex gap-1">
              {inactiveChannels.map((channel) => {
                const { color } = getPlatformInfo(channel.platform);
                const inactiveAvatarUrl = getChannelAvatar(channel.metadata as Record<string, unknown> | null | undefined) ?? undefined;
                return (
                  <Tooltip
                    key={channel.id}
                    content={`${getPlatformInfo(channel.platform).name}: ${channel.platformUsername} (Disconnected)`}
                  >
                    <div className="relative w-6 h-6 rounded-full opacity-40 grayscale cursor-not-allowed">
                      {inactiveAvatarUrl ? (
                        <img
                          src={inactiveAvatarUrl}
                          alt={channel.platformUsername}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full rounded-full flex items-center justify-center text-white text-[7px] font-semibold"
                          style={{ backgroundColor: color }}
                        >
                          {channel.platformUsername?.[0]?.toUpperCase() || '?'}
                        </div>
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

/**
 * Inline warning under the Instagram channel group when one or more selected
 * IG channels are configured for FEED but the post's images don't satisfy the
 * IG FEED aspect range (0.5625–1.91). Offers a one-click "Switch to Reels"
 * action that flips contentType=REELS for all FEED-mode channels in the group.
 *
 * Carousels: any single image out of range fails the whole post on Meta's
 * side, so we surface ALL violating items.
 */
interface IgAspectWarningProps {
  selectedChannels: Channel[];
  channelOptions: Record<number, PlatformOptions>;
  media: MediaItem[];
  onOptionsChange: (channelId: number, options: PlatformOptions) => void;
  /** Fires whenever the warning's hasAny state changes — parent uses this to
   *  auto-expand the IG section so the user sees channels alongside the alert. */
  onWarningChange?: (hasWarning: boolean) => void;
  disabled?: boolean;
}

function IgAspectWarning({
  selectedChannels,
  channelOptions,
  media,
  onOptionsChange,
  onWarningChange,
  disabled,
}: IgAspectWarningProps) {
  const { t } = useTranslation('common');

  // Group selected IG channels by their effective contentType (default FEED).
  // We only warn when there's at least one IMAGE that violates the channel's
  // configured contentType range — videos are not aspect-checked.
  const warning = useMemo(() => {
    const byType: Record<InstagramContentType, Channel[]> = { FEED: [], REELS: [], STORIES: [] };
    for (const ch of selectedChannels) {
      const ct = (channelOptions[ch.id]?.instagram?.contentType ?? 'FEED') as InstagramContentType;
      byType[ct].push(ch);
    }

    // Build a per-contentType issue list. Suppress entirely if dimensions are
    // unknown (composer can't confidently warn yet — backend guard catches it).
    const violations: Array<{
      contentType: InstagramContentType;
      channels: Channel[];
      issues: ReturnType<typeof checkInstagramAspect>['issues'];
      /** True only when EVERY violating image's aspect would pass REELS. If
       *  any image is out of range for both FEED and REELS, suggesting Reels
       *  is misleading — fall back to "crop the image". */
      canFixBySwitchingToReels: boolean;
    }> = [];
    for (const ct of ['FEED', 'REELS', 'STORIES'] as const) {
      if (byType[ct].length === 0) continue;
      const result = checkInstagramAspect(media, ct);
      if (!result.ok) {
        const reelsLimits = { min: 0.5625, max: 1.0 };
        const canFixBySwitchingToReels =
          ct === 'FEED' &&
          result.issues.every(i => i.aspect >= reelsLimits.min && i.aspect <= reelsLimits.max);
        violations.push({ contentType: ct, channels: byType[ct], issues: result.issues, canFixBySwitchingToReels });
      }
    }
    return violations;
  }, [selectedChannels, channelOptions, media]);

  // Notify parent ONLY on warning transitions (false → true), not on every
  // render. This lets the parent auto-expand the IG group when an issue first
  // appears while still respecting a subsequent manual collapse by the user.
  const lastWarningRef = useRef<boolean>(false);
  useEffect(() => {
    const hasWarning = warning.length > 0;
    if (hasWarning !== lastWarningRef.current) {
      lastWarningRef.current = hasWarning;
      onWarningChange?.(hasWarning);
    }
  }, [warning.length, onWarningChange]);

  if (warning.length === 0) return null;

  const handleSwitchToReels = (channels: Channel[]) => {
    for (const ch of channels) {
      const current = channelOptions[ch.id] ?? {};
      onOptionsChange(ch.id, {
        ...current,
        instagram: { ...(current.instagram ?? {}), contentType: 'REELS' },
      });
    }
  };

  /**
   * Build a concrete crop suggestion for the user based on the worst-aspect
   * issue in this violation set. Returns an i18n key — caller resolves it.
   */
  const cropHintKey = (
    issues: ReturnType<typeof checkInstagramAspect>['issues'],
    contentType: InstagramContentType
  ): string => {
    // Find the most-extreme aspect (furthest from 1.0) for the example.
    const worst = issues.reduce((acc, cur) =>
      Math.abs(Math.log(cur.aspect)) > Math.abs(Math.log(acc.aspect)) ? cur : acc
    );
    if (contentType === 'FEED') {
      // FEED: 0.5625 (vertical 9:16) – 1.91 (landscape ~1.91:1) — matches Meta's actual API range
      return worst.aspect < 0.5625
        ? 'ig_aspect_warning_crop_too_tall_feed'
        : 'ig_aspect_warning_crop_too_wide_feed';
    }
    // REELS / STORIES: 0.5625 (9:16) – 1.0 (1:1)
    return worst.aspect < 0.5625
      ? 'ig_aspect_warning_crop_too_tall_reels'
      : 'ig_aspect_warning_crop_generic_reels';
  };

  return (
    <div className="mt-2 pl-[38px] space-y-1.5">
      {warning.map(({ contentType, channels, issues, canFixBySwitchingToReels }) => {
        const channelLabel = channels.length === 1
          ? channels[0].platformUsername
          : t('ig_aspect_warning_n_channels', { count: channels.length });
        const firstIssue = issues[0];
        const andMore = issues.length > 1
          ? t('ig_aspect_warning_and_more', { count: issues.length - 1 })
          : '';
        return (
          <div
            key={contentType}
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug"
            role="alert"
          >
            <span className="material-symbols-outlined flex-shrink-0 text-amber-600" style={{ fontSize: 14 }}>
              warning
            </span>
            <div className="flex-1 text-amber-900">
              {t('ig_aspect_warning_will_not_post', {
                channel: channelLabel,
                contentType,
                width: firstIssue.width,
                height: firstIssue.height,
                aspect: firstIssue.aspect.toFixed(2),
                andMore,
              })}{' '}
              {canFixBySwitchingToReels && !disabled ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleSwitchToReels(channels)}
                    className="font-medium text-amber-900 underline hover:text-amber-950"
                  >
                    {t('ig_aspect_warning_switch_to_reels_action')}
                  </button>
                  {t('ig_aspect_warning_switch_to_fix')}
                </>
              ) : (
                t(cropHintKey(issues, contentType))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
